/**
 * What the chart screen does while it is waiting.
 *
 * Two decisions live here, both of which look obvious and are not:
 *
 * - **Whether a load blanks the chart.** Blanking the wrong load flickers;
 *   not blanking the right one leaves the previous instrument on screen under
 *   a new name, which `chart-series.test.ts` exists because of.
 * - **When a loading indicator is allowed to appear.** Too eager and it
 *   flashes on every fast load; too patient and the screen is blank with no
 *   explanation, which is the state this was written to fix.
 *
 * Both are arithmetic on strings and milliseconds, so they are testable here
 * rather than by watching a device and guessing — the same reason `view.ts`
 * and `scale.ts` are their own files.
 *
 * ## Why this is in api-client and not in either app
 *
 * It started in `apps/mobile`, which is where the blank chart was reported.
 * Every line of it turned out to be equally true of the web: the same request,
 * the same two failure modes, the same wording. `delayLabel` in `format.ts` set
 * the precedent — display copy that must read identically on every surface
 * belongs beside the client that fetches the data, not duplicated into each
 * app to drift apart one edit at a time.
 */

/** The instrument half of a `symbol:timeframe` key. */
function instrumentOf(key: string): string {
  const at = key.lastIndexOf(':');
  return at === -1 ? key : key.slice(0, at);
}

/**
 * Should the candles on screen be thrown away before this load?
 *
 * **Only a different instrument blanks the chart.** A timeframe change is the
 * same instrument at another aggregation, and blanking for it put a
 * full-screen loader on every 5m→15m tap — a flash of nothing for a request
 * that usually answers in well under a second.
 *
 * A different SYMBOL is not that. Leaving CL's candles drawn under EURUSD's
 * name is not stale data, it is wrong data with every badge on the screen
 * reading correct, and it is the bug `chart-series.test.ts` was written for.
 * So the instrument is the line, and it is the only line.
 *
 * The stale candles that survive a timeframe change are shown dimmed rather
 * than at full strength — visibly old, not silently wrong. That is the
 * renderer's job; this only decides.
 *
 * `null` means nothing has been loaded yet, which blanks trivially: there is
 * nothing on screen to keep.
 */
export function blanksChart(loaded: string | null, next: string): boolean {
  if (loaded === null) return true;
  return instrumentOf(loaded) !== instrumentOf(next);
}

// ---------------------------------------------------------------------------
// How long is too long
// ---------------------------------------------------------------------------

/**
 * Below this, show nothing at all.
 *
 * A loader that appears and vanishes inside a fifth of a second reads as a
 * glitch rather than as progress. Most loads on a warm connection finish
 * inside this window, so most of the time the chart simply appears.
 */
export const SHOW_AFTER_MS = 250;

/**
 * Past this, say so.
 *
 * Not an error — the request is still in flight and will probably land. But
 * five seconds of an animation that looks identical at second one and second
 * five tells the reader nothing about which they are in.
 */
export const SLOW_AFTER_MS = 5_000;

/**
 * Past this, offer a way out.
 *
 * The animation keeps running, because the request has not actually failed and
 * saying it has would be a lie. What changes is that there is now something to
 * press — a chart that never loads and a chart about to load look identical,
 * and the difference costs nothing to offer.
 */
export const STALLED_AFTER_MS = 15_000;

export type LoadStage = 'hidden' | 'loading' | 'slow' | 'stalled';

/**
 * How much to say, given how long it has taken.
 *
 * Deliberately monotonic and total: every non-negative number maps to exactly
 * one stage, and the stages only ever escalate. A loader that could go
 * backwards would flicker between two messages on a slow connection.
 */
export function loadStage(elapsedMs: number): LoadStage {
  if (!Number.isFinite(elapsedMs) || elapsedMs < SHOW_AFTER_MS) return 'hidden';
  if (elapsedMs < SLOW_AFTER_MS) return 'loading';
  if (elapsedMs < STALLED_AFTER_MS) return 'slow';
  return 'stalled';
}

/**
 * What the label under the mark says.
 *
 * The symbol is in it because "Loading…" on a screen with a symbol strip, a
 * price header and a timeframe row does not say WHICH of those it is loading —
 * and on a slow connection the one thing a reader wants confirmed is that the
 * app is fetching the instrument they just tapped.
 *
 * Upper case with the brand first, matching every other label on the phone
 * (`ALL 6 RULES`, `OPEN POSITIONS`). A sentence here would be the only
 * sentence-cased label in the app.
 */
export function loadingLabel(symbol: string | null): string {
  return symbol ? `AEGIS · LOADING ${symbol.toUpperCase()}` : 'AEGIS · CHOOSING A SYMBOL';
}
